// What to call an `ipfs://` reference in a pill. A file is its CID. A DAG-CBOR
// object resolves further: a bundled schema blob is named by the page that
// defines it (a schema carries no name of its own); an instance that links its
// schema (`schema: {"/": cid}`) is named by that type.
// Bundled library schemas resolve synchronously; anything else is fetched
// (react-query dedupes, so a page full of pills costs one request per CID).
import {useCID} from '@shm/shared/models/entity'
import {isDagJsonLink, parseCidString} from './dag-json'
import {isHypermediaSchema, nameForCid, HM_SCHEMAS, schemaForCid} from './schema/engine'
import {HM_SCHEMA_PAGES} from './schema/schema-registry.generated'
import {useSchemaRegistry} from './schema/schema-registry-cid'

const DAG_CBOR_CODE = 0x71

export type IpfsObjectKind = 'file' | 'object' | 'schema' | 'instance'

export function shortCid(cid: string): string {
  return cid.length > 18 ? `${cid.slice(0, 9)}…${cid.slice(-6)}` : cid
}

/** The name of a schema: its bundled page's name; a legacy root `name` on the blob; else the fallback. */
function schemaName(schema: Record<string, any> | undefined, fallback: string, cid?: string): string {
  const slug = cid ? nameForCid(cid) : undefined
  const page = slug ? HM_SCHEMA_PAGES[slug] : undefined
  if (page?.name) return page.name
  return schema && typeof schema.name === 'string' && schema.name ? schema.name : fallback
}

/** Resolve a pill label for an ipfs reference. `label` is always usable (falls back to the short CID). */
export function useIpfsObjectLabel(cid: string): {kind: IpfsObjectKind; label: string; title: string; named: boolean} {
  const isObject = parseCidString(cid)?.code === DAG_CBOR_CODE
  // Bundled schema CIDs need no fetch.
  const bundledName = isObject ? nameForCid(cid) : undefined
  const bundled = bundledName ? HM_SCHEMAS[bundledName] : undefined
  const blob = useCID(isObject && !bundled ? cid : undefined)
  const value = blob.data?.value as Record<string, any> | undefined
  const valueIsSchema = !!value && isHypermediaSchema(value)
  const linkedSchemaCid =
    value && !valueIsSchema && isDagJsonLink(value.schema) && parseCidString(value.schema['/'])?.code === DAG_CBOR_CODE
      ? (value.schema['/'] as string)
      : undefined
  const registry = useSchemaRegistry(linkedSchemaCid && !schemaForCid(linkedSchemaCid) ? [linkedSchemaCid] : [])
  const linkedSchema = linkedSchemaCid ? schemaForCid(linkedSchemaCid) ?? registry.byCid[linkedSchemaCid] : undefined

  const title = `ipfs://${cid}`
  // Unnamed targets show the whole CID (CSS-truncated) — a 9…6 excerpt says nothing.
  if (!isObject) return {kind: 'file', label: cid, title, named: false}
  if (bundled)
    return {kind: 'schema', label: schemaName(bundled, bundledName!, cid), title: `${title} — schema`, named: true}
  if (valueIsSchema)
    return {kind: 'schema', label: schemaName(value, 'Schema'), title: `${title} — schema`, named: true}
  if (linkedSchemaCid)
    return {
      kind: 'instance',
      label: linkedSchema ? schemaName(linkedSchema, cid, linkedSchemaCid) : cid,
      title: `${title} — a ${
        linkedSchema ? schemaName(linkedSchema, 'typed object', linkedSchemaCid) : 'typed object'
      }`,
      named: !!linkedSchema,
    }
  return {kind: 'object', label: cid, title, named: false}
}
